import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Box, IconButton, Button, CircularProgress } from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import PreviewIcon from "@mui/icons-material/Preview";
import UndoIcon from "@mui/icons-material/Undo";
import CalculateIcon from "@mui/icons-material/Calculate";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PostAddIcon from "@mui/icons-material/PostAdd";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AddTechnicalSanctionIcon from "@mui/icons-material/LibraryAddOutlined";
import FeedIcon from "@mui/icons-material/Feed";
import RequestPageIcon from "@mui/icons-material/RequestPage";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CancelPresentationIcon from "@mui/icons-material/CancelPresentation";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import PropTypes from "prop-types";

import ApplicationFormPreviewModal from "Components/Common/ApplicationFormPreviewModal";
import ApplicationStatusHistoryChart from "Components/Charts/ApplicationStatusHistoryChart";
import Modal from "Components/Common/Modal";
import { ChartIcon } from "Components/Common/Styled/ChartIcon";
import Backdrop from "Components/Common/Backdrop";
import Tooltip from "Components/Common/Tooltip";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import {
  CONNECTION_RELEASED_STATUS,
  DCS_CONSUMER_APPLICATION_INCHARGE,
  DCS_CONSUMER_APPLICATION_OPERATOR,
  DCS_DEPARTMENT_HEAD,
  ESTIMATE_DEPOSITED_STATUS,
  PROCESSED_STATUS,
  SEWAGE_CONSUMER_APPLICATION_INCHARGE,
  SEWAGE_CONSUMER_APPLICATION_OPERATOR,
  SEWAGE_DEPARTMENT_HEAD,
  SOLID_WASTE_CONSUMER_APPLICATION_INCHARGE,
  SOLID_WASTE_CONSUMER_APPLICATION_OPERATOR,
  SOLID_WASTE_DEPARTMENT_HEAD,
  SUPER_ADMIN,
  WATER_CONSUMER_APPLICATION_INCHARGE,
  WATER_CONSUMER_APPLICATION_OPERATOR,
  WATER_DEPARTMENT_HEAD,
  applicationStatusTypes,
  applicationTypes,
  estimateIssued,
} from "common/constant";
import { getOptionValue } from "common/enums";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import InputFieldText from "Components/Common/InputFieldText";
import PreviewPendingDocuments from "pages/Admin/PowerApplicationRequest/PreviewPendingDocuments";
import TechnicalSanctionFormForOtherUtility from "pages/Admin/TechnicalSanction/TechnicalSanctionFormForOtherUtility";
import TechnicalSanctionApprovalButtons from "pages/Admin/PowerApplicationRequest/TechnicalSanctionApprovalButtons";
import RefundBankDetailsFormModal from "pages/Admin/PowerApplicationRequest/RefundBankDetailsFormModal";
import DemandEstimateForm from "./DemandEstimateForm";
import DemandEstimationApprovalButtons from "pages/Admin/PowerApplicationRequest/DemandEstimationApprovalButtons";
import SettlementChargeApproveButtons from "./SettlementChargeApproveButtons";
import MeterReadingDetailsModal from "./MeterReadingDetailsModal";
import MeterInfoForm from "./MeterInfoForm";
import SettlementChargeForm from "./SettlementChargeForm";
import RefundCalculationForm from "./RefundCalculationForm";

import { filterEmptyStrings, getImageUrl, isAuthorizedUser } from "utils";
import getFileExtension from "utils/getFileExtension";
import { getUtilityWiseRoles } from "utils/getUtilityWiseRoles";

import {
  usePutReleaseConnectionMutation,
  usePutRemoveMeterMutation,
} from "apis/ApplicationRequestDetail";
import {
  useLazyGetTechnicalSanctionDataByIdQuery,
  usePutRevokeTechnicalSanctionMutation,
} from "apis/TechnicalSanction";
import { useLazyAdminCommentQuery } from "apis/ApplicationRequestComment";
import {
  useLazyGetDemandEstimationByApplicationIdQuery,
  usePostDemandEstimationForOtherUtilityMutation,
  usePutDemandEstimationDocumentApproveMutation,
} from "apis/DemandEstimation";
import { useLazyGetOtherApplicationDetailQuery } from "apis/otherApplicationDetails";

import {
  PreviewPendingDocumentsIcon,
  ViewTechnicalSanctionDocumentIcon,
  ViewDemandEstimationDocumentIcon,
  AddEditMeterIcon,
} from "./style";

const pendingDocumentNonUploadableStatus = [
  "saved",
  "submitted",
  "reviewed",
  "rejected",
];

const allowRoles = [
  SUPER_ADMIN,
  SOLID_WASTE_CONSUMER_APPLICATION_INCHARGE,
  SEWAGE_CONSUMER_APPLICATION_INCHARGE,
  WATER_CONSUMER_APPLICATION_INCHARGE,
  DCS_CONSUMER_APPLICATION_INCHARGE,
];

const allowRolesForUploadingSignedDocuments = [
  ...allowRoles,
  DCS_CONSUMER_APPLICATION_OPERATOR,
  WATER_CONSUMER_APPLICATION_OPERATOR,
  SEWAGE_CONSUMER_APPLICATION_OPERATOR,
  SOLID_WASTE_CONSUMER_APPLICATION_OPERATOR,
];

const allowRolesForRevokeApplication = [
  SUPER_ADMIN,
  DCS_DEPARTMENT_HEAD,
  WATER_DEPARTMENT_HEAD,
  SEWAGE_DEPARTMENT_HEAD,
  SOLID_WASTE_DEPARTMENT_HEAD,
];

const NON_DOMESTIC_PREMISE_TYPE = "Non-Domestic";

const OtherApplicationActions = ({ item, t, viewApplicationBtnHandler }) => {
  const {
    id,
    approvedApplicationDocumentURL,
    applicationType: { id: applicationTypeId, name: applicationTypeName },
    applicationStatus,
    utilityAddress,
    isPartialDocumentsUploaded,
    isAllPendingDocumentsApproved,
    groupId,
    bankDetailId,
    isMeterAssigned,
    meterId,
    utilities,
    finalRefundAmount,
    isRefundCalculatedByFinance,
    isTechnicalFeasibility,
    refundCalculationBreakupUrl,
    technicalSanctionUrl,
    isTechnicalSanctionApproved,
    isTemporaryConnection,
    isTechnicalSanctionRequired,
    technicalSanctionRevokedBy,
    isTechnicalSanctionReviewed,
    technicalSanctionId: technicalSanctionDataId,
    demandEstimateId,
    demandEstimateApprover,
    tariffId,
    refundCalculationUrl,
    demandEstimateGrandTotal,
    taxInvoiceUrl,
    demandEstimateUrl,
    isMeterRemoved,
    isDemandEstimateReviewed,
    utilityConsumerId,
    isDemandEstimateDocumentApproved,
    settlementOfConnectionUrl,
    settlementOfConnectionCharge,
    isSettlementConnectionChargeDocumentApproved,
    isSettlementChargeRefundRequiredToConsumer,
  } = item;
  const [getApplicationData, { isLoading: isApplicationDataLoading }] =
    useLazyGetOtherApplicationDetailQuery();

  const [getDemandEstimation, { isLoading: isDemandEstimationDataLoading }] =
    useLazyGetDemandEstimationByApplicationIdQuery();

  const [addDemandEstimation, { isLoading: isDemandEstimateGenerating }] =
    usePostDemandEstimationForOtherUtilityMutation();

  const [removeMeter, { isMeterRemoving }] = usePutRemoveMeterMutation();

  const [
    applicationDataForMeterReadingModal,
    setApplicationDataForMeterReadingModal,
  ] = useState(null);
  const [isRefundCalculationModalOpen, setIsRefundCalculationModalOpen] =
    useState(false);

  const [
    getTechnicalSanctionDataByTechnicalSanctionId,
    { isLoading: isTechnicalSanctionDataLoading },
  ] = useLazyGetTechnicalSanctionDataByIdQuery();

  // Get Admin Comments
  const [getAdminComments, { isLoading: isAdminCommentsLoading }] =
    useLazyAdminCommentQuery();

  const [revokeApplication, { isLoading: isRevokedApplicationLoading }] =
    usePutRevokeTechnicalSanctionMutation();

  const [uploadSignedDemandEstimate, { isLoading }] =
    usePutDemandEstimationDocumentApproveMutation();

  const [releaseConnection, { isLoading: isConnectionReleasing }] =
    usePutReleaseConnectionMutation();

  const { roles, user: { id: userId } = {} } = useSelector(
    (state) => state.Login
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const technicalSanctionId = searchParams.get("technicalSanctionId");
  const demandEstimationId = searchParams.get("demandEstimationId");

  const ref = useRef();

  const [isApplicationFormModalOpen, setIsApplicationFormModalOpen] =
    useState(false);
  const [applicationIdForChartModal, setApplicationIdForChartModal] =
    useState("");
  const [partiallyUploadedDocuments, setPartiallyUploadedDocuments] = useState(
    []
  );
  const [
    applicationIdForMeterReadingDetailsModal,
    setApplicationIdForMeterReadingDetailsModal,
  ] = useState("");

  const [isTechnicalSanctionModalOpen, setIsTechnicalSanctionModalOpen] =
    useState(false);
  const [selectedApplicationDetails, setSelectedApplicationDetails] = useState(
    {}
  );
  const [utilityConsumerIdForRemoveMeter, setUtilityConsumerIdForRemoveMeter] =
    useState(false);

  const [applicationIdForDemandEstimate, setApplicationIdForDemandEstimate] =
    useState("");
  const [isSettlementChargeModelOpen, setIsSettleMentChargeModelOpen] =
    useState(false);

  const [refundAmountPaymentModalProps, setRefundAmountPaymentModalProps] =
    useState(null);

  const [isAddSapNumberModalOpen, setIsAddSapNumberModalOpen] = useState(false);

  const [confirmationModalProps, setConfirmationModalProps] = useState(null);
  const [documentToPreview, setDocumentToPreview] = useState();

  const isSolidWasteApplication = useMemo(
    () => utilities[0] === getOptionValue("utilityOptions", "Solid Waste"),
    [utilities]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      salesOrder: "",
      serialNumber: "",
    },
    validationSchema: Yup.object({
      salesOrder: Yup.string()
        .required("Sales order is required")
        .test({
          name: "isValidSalesOrder",
          test: (value) => /^[a-zA-Z0-9]{10}$/.test(value),
          message: "Sales order length must be 10 digits",
        }),

      ...(isSolidWasteApplication && {
        serialNumber: Yup.string()
          .required("Serial number is required")
          .test({
            name: "isValidSerialNumber",
            test: (value) => /^[a-zA-Z0-9]*$/.test(value),
            message: "Please enter valid serial number",
          }),
      }),
    }),
    onSubmit: ({ salesOrder, serialNumber }) => {
      releaseConnectionBtnHandler(salesOrder, serialNumber);
    },
  });

  const applicationStatusName = useMemo(
    () => applicationStatus?.name?.toLowerCase() ?? "",
    [applicationStatus]
  );

  const { isInProcessedStatus, isInEstimateDepositedStatus } = useMemo(
    () => ({
      isInProcessedStatus: applicationStatusName === PROCESSED_STATUS,
      isInEstimateDepositedStatus:
        applicationStatusName === ESTIMATE_DEPOSITED_STATUS,
    }),
    [applicationStatusName]
  );

  const isPreviewPendingDocumentsIconVisible = useMemo(
    () =>
      !pendingDocumentNonUploadableStatus.includes(applicationStatusName) &&
      !isAllPendingDocumentsApproved &&
      isPartialDocumentsUploaded,
    [
      isPartialDocumentsUploaded,
      isAllPendingDocumentsApproved,
      applicationTypeName,
      applicationStatusName,
    ]
  );
  const isSettlementIconVisible = useMemo(
    () =>
      utilities[0] === getOptionValue("utilityOptions", "DCS") &&
      [
        applicationTypes["Permanent Connection"],
        applicationTypes["Temporary Connection"],
      ].includes(applicationTypeId) &&
      isAuthorizedUser(roles, [
        ...getUtilityWiseRoles([utilities[0]]).admin3Roles,
        SUPER_ADMIN,
      ]) &&
      applicationStatusName === ESTIMATE_DEPOSITED_STATUS &&
      (utilityAddress?.premiseType.name === NON_DOMESTIC_PREMISE_TYPE ||
        utilityAddress?.dcsDomesticPremiseType ===
          getOptionValue("dcsConnectionTypeEnum", "Building")) &&
      !isSettlementConnectionChargeDocumentApproved,
    [
      applicationStatusName,
      isSettlementConnectionChargeDocumentApproved,
      utilities,
      applicationTypeId,
      utilityAddress,
    ]
  );

  const isPostConnectionApplications = useMemo(
    () =>
      ![
        applicationTypes["Permanent Connection"],
        applicationTypes["Temporary Connection"],
      ].includes(applicationTypeId),
    [applicationTypeId]
  );

  const isTechnicalSanctionGenerateIconVisible = useMemo(
    () =>
      isInProcessedStatus &&
      isAuthorizedUser(roles, [
        ...getUtilityWiseRoles([utilities[0]]).admin3Roles,
        SUPER_ADMIN,
      ]) &&
      isTechnicalSanctionRequired &&
      !isTechnicalSanctionApproved &&
      (!!technicalSanctionRevokedBy || !isTechnicalSanctionReviewed),
    [
      isInProcessedStatus,
      roles,
      utilities,
      SUPER_ADMIN,
      isTechnicalSanctionRequired,
      isTechnicalSanctionApproved,
      technicalSanctionRevokedBy,
      isTechnicalSanctionReviewed,
    ]
  );

  const isDemandAlterationApplication = useMemo(
    () =>
      [
        applicationTypes["Demand Reduction"],
        applicationTypes["Demand Extension"],
      ].includes(applicationTypeId),
    [applicationTypeId]
  );

  const isDcsUtility = utilities?.some(
    (value) => value === getOptionValue("utilityOptions", "DCS")
  );

  const isConnectionReleaseIconVisible = useMemo(
    () =>
      (isAuthorizedUser(roles, [
        ...getUtilityWiseRoles([utilities[0]]).admin3Roles,
        SUPER_ADMIN,
      ]) &&
        ((applicationTypeId === applicationTypes["Temporary Connection"] &&
          utilities[0] === getOptionValue("utilityOptions", "Solid Waste") &&
          isInProcessedStatus) ||
          (applicationTypeId === applicationTypes["Name Change/Transfer"] &&
            isInProcessedStatus) ||
          (applicationStatusName === ESTIMATE_DEPOSITED_STATUS &&
            isMeterAssigned &&
            settlementOfConnectionCharge >= 0 &&
            isSettlementConnectionChargeDocumentApproved) ||
          (applicationTypeId === applicationTypes["Removal of Connection"] &&
            isRefundCalculatedByFinance &&
            ((applicationStatus.id === applicationStatusTypes["Disconnected"] &&
              finalRefundAmount === 0 &&
              !!refundCalculationBreakupUrl) ||
              (finalRefundAmount < 0 && isInEstimateDepositedStatus))))) ||
      (((finalRefundAmount > 0 &&
        !!bankDetailId &&
        applicationStatus.id === applicationStatusTypes["Disconnected"]) ||
        (isDemandAlterationApplication &&
          !!bankDetailId &&
          demandEstimateGrandTotal < 0 &&
          applicationStatusName === estimateIssued) ||
        (applicationStatusName === ESTIMATE_DEPOSITED_STATUS &&
          isMeterAssigned &&
          settlementOfConnectionCharge < 0 &&
          isSettlementConnectionChargeDocumentApproved)) &&
        isAuthorizedUser(roles, [
          ...getUtilityWiseRoles([utilities[0]]).admin2Roles,
          SUPER_ADMIN,
        ])),
    [
      applicationTypeId,
      applicationStatusName,
      isInProcessedStatus,
      utilities,
      isDemandAlterationApplication,
      demandEstimateGrandTotal,
      bankDetailId,
      isMeterAssigned,
      roles,
    ]
  );

  const isSettlementChargeRefundIconVisible = useMemo(
    () =>
      (applicationTypeId === applicationTypes["Permanent Connection"] ||
        applicationTypeId === applicationTypes["Temporary Connection"]) &&
      applicationStatusName === CONNECTION_RELEASED_STATUS &&
      isMeterAssigned &&
      settlementOfConnectionCharge < 0 &&
      isSettlementConnectionChargeDocumentApproved &&
      isSettlementChargeRefundRequiredToConsumer &&
      isAuthorizedUser(roles, [
        ...getUtilityWiseRoles([utilities[0]]).admin2Roles,
        SUPER_ADMIN,
      ]),
    [
      utilities,
      applicationTypeId,
      roles,
      applicationStatusName,
      isMeterAssigned,
      settlementOfConnectionCharge,
      isSettlementConnectionChargeDocumentApproved,
      isSettlementChargeRefundRequiredToConsumer,
    ]
  );

  const isAddUpdateMeterIconVisible = useMemo(
    () =>
      !isDemandAlterationApplication &&
      ![
        getOptionValue("utilityOptions", "Sewage"),
        getOptionValue("utilityOptions", "Solid Waste"),
      ].includes(utilities[0]) &&
      isAuthorizedUser(roles, [
        ...getUtilityWiseRoles([utilities[0]]).admin3Roles,
        SUPER_ADMIN,
      ]) &&
      isSettlementConnectionChargeDocumentApproved &&
      ((applicationStatusName === ESTIMATE_DEPOSITED_STATUS &&
        ((settlementOfConnectionCharge < 0 && !!bankDetailId) ||
          settlementOfConnectionCharge >= 0)) ||
        (applicationStatusName === PROCESSED_STATUS &&
          applicationTypes["Removal of Connection"] === applicationTypeId &&
          !isMeterRemoved)),
    [
      utilities,
      utilityAddress,
      applicationStatusName,
      isDemandAlterationApplication,
      roles,
      applicationTypeId,
      bankDetailId,
      isPostConnectionApplications,
      settlementOfConnectionCharge,
      isSettlementConnectionChargeDocumentApproved,
      isMeterRemoved,
    ]
  );

  const isRemoveMeterIconVisible = useMemo(
    () =>
      isInProcessedStatus &&
      isMeterAssigned &&
      !isMeterRemoved &&
      applicationTypeId === applicationTypes["Removal of Connection"],
    [isInProcessedStatus, isMeterAssigned, isMeterRemoved]
  );

  const isRefundCalculationIconVisible = useMemo(
    () =>
      applicationStatus.id === applicationStatusTypes["Disconnected"] &&
      applicationTypeId === applicationTypes["Removal of Connection"] &&
      isMeterRemoved &&
      ((isAuthorizedUser(
        roles,
        ...getUtilityWiseRoles([utilities[0]]).admin3Roles
      ) &&
        !refundCalculationBreakupUrl) ||
        ((roles.includes(SUPER_ADMIN) ||
          isAuthorizedUser(
            roles,
            ...getUtilityWiseRoles([utilities[0]]).admin2Roles
          )) &&
          refundCalculationBreakupUrl)),
    [
      applicationStatus,
      applicationTypeId,
      isMeterRemoved,
      refundCalculationBreakupUrl,
      utilities,
      roles,
    ]
  );
  const isDemandEstimateGenerateIconVisible = useMemo(() => {
    if (
      applicationTypes["Temporary Connection"] === applicationTypeId &&
      utilities[0] === getOptionValue("utilityOptions", "Solid Waste")
    ) {
      return false;
    }

    return (
      ![
        applicationTypes["Name Change/Transfer"],
        applicationTypes["Removal of Connection"],
      ].includes(applicationTypeId) &&
      isInProcessedStatus &&
      isTechnicalSanctionApproved &&
      !!utilities.length &&
      (!demandEstimateApprover ||
        isAuthorizedUser(
          [demandEstimateApprover?.roleName],
          [...getUtilityWiseRoles([utilities[0]]).admin3Roles]
        )) &&
      isAuthorizedUser(roles, allowRoles)
    );
  }, [
    applicationTypeId,
    applicationTypes,
    roles,
    isInProcessedStatus,
    isTechnicalSanctionApproved,
    utilities,
    demandEstimateApprover,
  ]);

  const releaseConnectionBtnHandler = async (
    salesOrder = "",
    serialNumber = ""
  ) => {
    try {
      await releaseConnection({
        applicationId: id,
        toastMessage: isPostConnectionApplications
          ? "Application process completed"
          : "Connection released",
        query: filterEmptyStrings({ salesOrder, serialNumber }),
      });
      setConfirmationModalProps(null);
      setIsAddSapNumberModalOpen(false);
    } catch (error) {}
  };

  const setConfirmationModalPropsForReleaseConnection = () =>
    setConfirmationModalProps({
      title: "Warning",
      message: "Are you sure you want to complete this application?",
      onCancel: () => setConfirmationModalProps(null),
      onConfirm: () => releaseConnectionBtnHandler(),
      variant: "warning",
    });

  useEffect(() => {
    (async () => {
      try {
        if (
          technicalSanctionId &&
          +technicalSanctionId === technicalSanctionDataId
        ) {
          const { data: { url, nextApproveBy, nextRevokeBy } = {} } =
            await getTechnicalSanctionDataByTechnicalSanctionId(
              technicalSanctionId
            );

          const { data: commentsData } = await getAdminComments({
            commentType: getOptionValue("adminComments", "TechnicalSanction"),
            commentTypeId: technicalSanctionId,
          });

          setDocumentToPreview({
            url: getImageUrl(url),
            documentTypeName: "Technical Sanction Document",
            documentType: "pdf",
            documentName: "technicalSanction",
            handleClose: () => {
              setDocumentToPreview(null);
              searchParams.delete("technicalSanctionId");
              setSearchParams(searchParams);
            },

            actionButton: (
              <TechnicalSanctionApprovalButtons
                technicalSanctionData={{
                  nextApproveBy,
                  nextRevokeBy,
                  applicationId: id,
                  technicalSanctionDataId,
                }}
                isTechnicalSanctionApproved={isTechnicalSanctionApproved}
                handleClose={() => {
                  setDocumentToPreview(null);
                  searchParams.delete("technicalSanctionId");
                  setSearchParams(searchParams);
                }}
                commentsData={commentsData ?? []}
              />
            ),
          });
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [technicalSanctionId, technicalSanctionDataId]);

  useEffect(() => {
    (async () => {
      try {
        if (demandEstimationId && +demandEstimationId === demandEstimateId) {
          const { data: { url } = {} } = await getDemandEstimation(id);
          setDocumentToPreview({
            url: getImageUrl(url),
            documentTypeName: "Demand Estimation Document",
            documentType: "pdf",
            documentName: "demandEstimation",

            handleClose: () => {
              setDocumentToPreview(null);
              searchParams.delete("demandEstimationId");
              setSearchParams(searchParams);
            },
            actionButton: !!demandEstimateUrl &&
              !isDemandEstimateReviewed &&
              demandEstimateApprover?.userId?.includes(userId) && (
                <DemandEstimationApprovalButtons
                  DemandEstimationData={{
                    applicationId: id,
                  }}
                  handleClose={() => {
                    setDocumentToPreview(null);
                    searchParams.delete("demandEstimationId");
                    setSearchParams(searchParams);
                  }}
                />
              ),
          });
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [demandEstimationId, demandEstimateId]);

  const handleUploadFile = async (event) => {
    try {
      const file = event.target.files[0];
      const fileExtension = getFileExtension(file?.name).toLowerCase();
      const fileSize = file?.size;
      if (fileSize > 2000000) {
        toast.error("File size should be less than 2MB");
      } else if (fileExtension !== "pdf") {
        toast.error("Invalid file format, Please upload pdf file");
      } else {
        setDocumentToPreview({
          url: URL.createObjectURL(file),
          documentTypeName: "Demand Estimate",
          documentType: "pdf",
          documentName: "demandEstimate",

          handleClose: () => {
            setDocumentToPreview(null);
          },

          actionButton: (
            <Button
              onClick={() => uploadSignedDemandEstimateHandler(file)}
              color="primary"
              className="mb-1 me-1"
              variant="contained"
              disabled={isLoading}
            >
              {t("Upload")} {isLoading && <CircularProgress size={20} />}
            </Button>
          ),
        });

        ref.current.value = "";
      }
    } catch (err) {}
  };

  const handleIssueDemandEstimation = async (applicationId) => {
    try {
      await addDemandEstimation(applicationId);
      setConfirmationModalProps(null);
    } catch (error) {
      console.error(error);
    }
  };

  const uploadSignedDemandEstimateHandler = async (uploadedFile) => {
    try {
      const formData = new FormData();
      formData.append("file", uploadedFile);
      formData.append("applicationIds[0]", id);
      formData.append("isApproved", true);

      await uploadSignedDemandEstimate(formData);

      setDocumentToPreview(null);
    } catch (error) {}
  };

  const revokeTechnicalSanctionBtnHandler = async () => {
    try {
      await revokeApplication(id);
      setConfirmationModalProps(null);
    } catch (error) {}
  };

  const previewDocumentsBtnHandler = async (applicationId) => {
    const { data: { documents = [] } = {} } = await getApplicationData({
      applicationId,
    });
    const optionalDocuments = documents.filter(({ isApproved }) => !isApproved);
    if (optionalDocuments.length) {
      setPartiallyUploadedDocuments(optionalDocuments);
    }
  };

  return (
    <Box width="150px">
      <Backdrop
        open={
          isApplicationDataLoading ||
          isTechnicalSanctionDataLoading ||
          isAdminCommentsLoading ||
          isDemandEstimationDataLoading
        }
      />
      <ConfirmationDialog
        isOpen={utilityConsumerIdForRemoveMeter}
        title="Warning"
        message="Are you sure you want to remove this meter?"
        onCancel={() => setUtilityConsumerIdForRemoveMeter("")}
        onConfirm={async () => {
          try {
            await removeMeter(utilityConsumerIdForRemoveMeter, {
              isMeterRemoved: true,
              isForApplication: true,
            });
            setUtilityConsumerIdForRemoveMeter("");
          } catch (error) {}
        }}
        isLoading={isMeterRemoving}
        variant="warning"
      />
      {/*Partially uploaded documents preview modal */}
      {!!partiallyUploadedDocuments.length && (
        <Modal
          isOpen
          handleClose={() => {
            setPartiallyUploadedDocuments([]);
          }}
          title="Documents"
          maxWidth="md"
        >
          <PreviewPendingDocuments
            documents={partiallyUploadedDocuments}
            applicationId={id}
            handleClose={() => {
              setPartiallyUploadedDocuments([]);
            }}
          />
        </Modal>
      )}

      {/* Application status history chart modal*/}
      {!!applicationIdForChartModal && (
        <Modal
          isOpen
          handleClose={() => setApplicationIdForChartModal("")}
          title="Application Status History Chart"
          maxWidth="md"
        >
          <ApplicationStatusHistoryChart
            applicationId={applicationIdForChartModal}
          />
        </Modal>
      )}

      {/* Application form modal */}
      {isApplicationFormModalOpen && (
        <ApplicationFormPreviewModal
          url={getImageUrl(approvedApplicationDocumentURL)}
          groupId={groupId}
          applicationType={applicationTypeName}
          applicationId={id}
          utilities={utilities}
          applicationStatus={applicationStatusName}
          openApplicationForm={isApplicationFormModalOpen}
          handleClose={() => setIsApplicationFormModalOpen(false)}
        />
      )}

      {/* Generate Technical Sanction Form Modal */}
      {isTechnicalSanctionModalOpen && (
        <TechnicalSanctionFormForOtherUtility
          isTechnicalSanctionModalOpen={isTechnicalSanctionModalOpen}
          handleClose={() => {
            setIsTechnicalSanctionModalOpen(false);
            setSelectedApplicationDetails("");
          }}
          selectedApplicationDetails={selectedApplicationDetails}
          otherUtilities={true}
        />
      )}

      {/* Confirmation Modal */}
      {!!confirmationModalProps && (
        <ConfirmationDialog
          isOpen
          isLoading={
            isRevokedApplicationLoading ||
            isDemandEstimateGenerating ||
            isConnectionReleasing
          }
          {...confirmationModalProps}
        />
      )}

      {/*Document Preview Modal */}
      {!!documentToPreview && (
        <DocumentPreviewModal isOpen {...documentToPreview} />
      )}
      {isSettlementChargeModelOpen && (
        <SettlementChargeForm
          applicationData={item}
          handleClose={() => setIsSettleMentChargeModelOpen(false)}
        />
      )}

      {!!applicationDataForMeterReadingModal && (
        <MeterInfoForm
          applicationTypeId={applicationTypeId}
          applicationData={applicationDataForMeterReadingModal}
          handleClose={() => setApplicationDataForMeterReadingModal(null)}
        />
      )}
      {!!isRefundCalculationModalOpen && (
        <RefundCalculationForm
          isTemporaryConnection={isTemporaryConnection}
          applicationId={id}
          utilityConsumerId={utilityConsumerId}
          utilityId={utilities[0]}
          handleClose={() => setIsRefundCalculationModalOpen(false)}
        />
      )}

      {!!applicationIdForMeterReadingDetailsModal && (
        <MeterReadingDetailsModal
          applicationId={id}
          handleClose={() => setApplicationIdForMeterReadingDetailsModal("")}
        />
      )}

      {!!refundAmountPaymentModalProps && (
        <RefundBankDetailsFormModal
          utilityConsumerId={refundAmountPaymentModalProps.utilityConsumerId}
          applicationId={refundAmountPaymentModalProps.applicationId}
          isSettlementRefundProcess={
            refundAmountPaymentModalProps?.isSettlementRefundProcess || false
          }
          handleClose={() => {
            setRefundAmountPaymentModalProps(null);
          }}
        />
      )}

      {!!applicationIdForDemandEstimate && (
        <DemandEstimateForm
          applicationId={applicationIdForDemandEstimate}
          handleClose={() => setApplicationIdForDemandEstimate("")}
        />
      )}

      {isAddSapNumberModalOpen && (
        <Modal
          isOpen
          handleClose={() => {
            setIsAddSapNumberModalOpen(false);
            formik.resetForm();
          }}
          title="Add SAP Number"
        >
          <div className="mt-3">
            <InputFieldText
              formik={formik}
              name="salesOrder"
              label={t("Sales Order") + "*"}
              placeholder={t("Enter Sales Order")}
            />

            {isSolidWasteApplication && (
              <InputFieldText
                formik={formik}
                name="serialNumber"
                label={t("Serial Number") + "*"}
                placeholder={t("Enter Serial Number")}
              />
            )}
            <Button
              onClick={formik.handleSubmit}
              variant="contained"
              className="d-flex ms-auto gap-2"
              disabled={isLoading || isConnectionReleasing}
            >
              {t("Submit")}
              {(isLoading || isConnectionReleasing) && (
                <CircularProgress size={20} />
              )}
            </Button>
          </div>
        </Modal>
      )}

      {/*  view icon for application details */}
      <IconButton
        onClick={() => viewApplicationBtnHandler(id, applicationTypeName)}
      >
        <Tooltip title={t("View Application Details")}>
          <RemoveRedEyeIcon
            fontSize="small"
            color="primary"
            className="cursor-pointer"
          />
        </Tooltip>
      </IconButton>

      {/* Preview icon to view application form */}
      {!!approvedApplicationDocumentURL && (
        <IconButton
          onClick={() => {
            setIsApplicationFormModalOpen(!!approvedApplicationDocumentURL);
          }}
        >
          <Tooltip title={t("View Application Form")}>
            <PreviewIcon
              fontSize="small"
              color={approvedApplicationDocumentURL ? "primary" : "disabled"}
              className="cursor-pointer"
            />
          </Tooltip>
        </IconButton>
      )}

      {/* Generate Technical Sanction Icon */}
      {isTechnicalSanctionGenerateIconVisible && (
        <IconButton
          onClick={() => {
            setIsTechnicalSanctionModalOpen(true);
            setSelectedApplicationDetails(item);
          }}
        >
          <Tooltip title={t("Generate Technical Sanction")}>
            <AddTechnicalSanctionIcon
              fontSize="small"
              className="cursor-pointer"
              color="primary"
            />
          </Tooltip>
        </IconButton>
      )}

      {/* Preview Icon For Technical Sanction Document */}
      {!!technicalSanctionUrl && (
        <IconButton
          onClick={() => {
            searchParams.set("technicalSanctionId", technicalSanctionDataId);
            setSearchParams(searchParams);
          }}
        >
          <Tooltip title={t("View Technical Sanction Document")}>
            <ViewTechnicalSanctionDocumentIcon />
          </Tooltip>
        </IconButton>
      )}

      {/* Issue Demand Estimate */}
      {isDemandEstimateGenerateIconVisible && (
        <IconButton
          onClick={() => {
            if (
              getOptionValue("dcsConnectionTypeEnum", "Individual Units") ===
                utilityAddress?.dcsDomesticPremiseType &&
              utilities[0] === getOptionValue("utilityOptions", "DCS")
            ) {
              setApplicationIdForDemandEstimate(id);
            } else {
              setConfirmationModalProps({
                title: "Warning",
                message: "Are you sure you want to issue Demand Estimate?",
                onCancel: () => setConfirmationModalProps(null),
                onConfirm: () => handleIssueDemandEstimation(id),
                variant: "warning",
              });
            }
          }}
        >
          <Tooltip title={t("Issue Demand Estimation")}>
            <CalculateIcon
              fontSize="small"
              className="cursor-pointer"
              color="primary"
            />
          </Tooltip>
        </IconButton>
      )}

      {/* Preview icon to view demand estimation document */}
      {!!demandEstimateUrl && (
        <IconButton
          onClick={() => {
            searchParams.set("demandEstimationId", demandEstimateId);
            setSearchParams(searchParams);
          }}
        >
          <Tooltip title={t("View Demand Estimation Document")}>
            <ViewDemandEstimationDocumentIcon />
          </Tooltip>
        </IconButton>
      )}

      {isAuthorizedUser(roles, allowRolesForUploadingSignedDocuments) &&
        isDemandEstimateReviewed &&
        !isDemandEstimateDocumentApproved && (
          <IconButton size="small" color="primary" component="label">
            <Tooltip title={t("Upload Signed Demand Estimate")}>
              <FileUploadIcon />
              <input
                ref={ref}
                style={{
                  display: "none",
                }}
                accept=".pdf"
                type="file"
                onChange={(e) => handleUploadFile(e)}
              />
            </Tooltip>
          </IconButton>
        )}

      {isSettlementIconVisible && (
        <IconButton onClick={() => setIsSettleMentChargeModelOpen(true)}>
          <Tooltip title={t("Settlement Estimate Demand")}>
            <PriceChangeIcon color="primary" />
          </Tooltip>
        </IconButton>
      )}

      {settlementOfConnectionUrl && (
        <IconButton
          size="small"
          onClick={() => {
            setDocumentToPreview({
              url: getImageUrl(settlementOfConnectionUrl),
              documentTypeName: "Settlement Charge Document",
              documentType: "pdf",
              documentName: "Settlement Charge",
              handleClose: () => setDocumentToPreview(null),
              actionButton: (
                <SettlementChargeApproveButtons
                  DemandEstimationData={{
                    applicationId: id,
                    isSettlementConnectionChargeDocumentApproved:
                      isSettlementConnectionChargeDocumentApproved,
                    utilities,
                  }}
                  handleClose={() => {
                    setDocumentToPreview(null);
                  }}
                />
              ),
            });
          }}
        >
          <Tooltip title={t("Preview Settlement Charge Document")}>
            <FilePresentIcon color="primary" />
          </Tooltip>
        </IconButton>
      )}

      {/* Add or Update meter information */}
      {isAddUpdateMeterIconVisible && (
        <IconButton
          onClick={() =>
            setApplicationDataForMeterReadingModal({
              applicationTypeId: applicationTypeId,
              applicationId: id,
              consumerId: utilityConsumerId,
              meterId,
              utilityId: utilities[0],
              tariffId,
            })
          }
        >
          <Tooltip title={t("Add/Update Meter Information")}>
            <AddEditMeterIcon />
          </Tooltip>
        </IconButton>
      )}

      {!!refundCalculationUrl && (
        <Tooltip title={t("Refund Calculations Document")}>
          <IconButton
            onClick={() => {
              setDocumentToPreview({
                url: getImageUrl(refundCalculationUrl),
                documentTypeName: "Refund Calculations",
                documentType: "pdf",
                documentName: "Refund Calculations",
                handleClose: () => setDocumentToPreview(null),
              });
            }}
            size="small"
          >
            <InsertDriveFileIcon color="primary" />
          </IconButton>
        </Tooltip>
      )}

      {!!refundCalculationBreakupUrl && (
        <Tooltip title={t("Refund Calculations Breakup Document")}>
          <IconButton
            onClick={() => {
              setDocumentToPreview({
                url: getImageUrl(refundCalculationBreakupUrl),
                documentTypeName: "Refund Calculations Breakup Document",
                documentType: "pdf",
                documentName: "Refund Calculations Breakup",
                handleClose: () => setDocumentToPreview(null),
              });
            }}
            size="small"
          >
            <RequestPageIcon color="primary" />
          </IconButton>
        </Tooltip>
      )}

      {isRemoveMeterIconVisible && (
        <Tooltip
          title={
            [
              getOptionValue("utilityOptions", "Sewage"),
              getOptionValue("utilityOptions", "Solid Waste"),
            ].includes(utilities[0])
              ? t("Disconnect Consumer")
              : t("Remove Meter")
          }
        >
          <IconButton
            onClick={() =>
              setUtilityConsumerIdForRemoveMeter(utilityConsumerId)
            }
            size="small"
          >
            <CancelPresentationIcon color="primary" />
          </IconButton>
        </Tooltip>
      )}
      {isRefundCalculationIconVisible && (
        <Tooltip title={t("Refund Calculations")}>
          <IconButton
            onClick={() => {
              setIsRefundCalculationModalOpen(true);
            }}
            size="small"
          >
            <FeedIcon color="primary" />
          </IconButton>
        </Tooltip>
      )}

      {!!meterId &&
        applicationTypeId === applicationTypes["Removal of Connection"] && (
          <IconButton
            onClick={() => setApplicationIdForMeterReadingDetailsModal(id)}
          >
            <Tooltip title={t("Meter Reading Details")}>
              <AssignmentIcon color="primary" />
            </Tooltip>
          </IconButton>
        )}

      {/* Revoke Application */}
      {(applicationStatus.id === applicationStatusTypes["Rejected"] &&
        !isTechnicalFeasibility ||
                applicationStatus.id === applicationStatusTypes["Expired"]) &&
        isAuthorizedUser(roles, allowRolesForRevokeApplication) && (
          <IconButton
            onClick={() => {
              setConfirmationModalProps({
                title: "Warning",
                message: "Are you sure you want to revoke this application?",
                onCancel: () => setConfirmationModalProps(null),
                onConfirm: revokeTechnicalSanctionBtnHandler,
                variant: "warning",
              });
            }}
          >
            <Tooltip title={t("Revoke Application")}>
              <UndoIcon
                fontSize="small"
                className="cursor-pointer"
                color="primary"
              />
            </Tooltip>
          </IconButton>
        )}

      {isConnectionReleaseIconVisible && (
        <IconButton
          onClick={() => {
            if (
              (isDemandAlterationApplication && demandEstimateGrandTotal < 0) ||
              (applicationTypeId ===
                applicationTypes["Removal of Connection"] &&
                finalRefundAmount > 0) ||
              settlementOfConnectionCharge < 0
            ) {
              if (
                (applicationTypeId ===
                  applicationTypes["Permanent Connection"] ||
                  applicationTypeId ===
                    applicationTypes["Temporary Connection"]) &&
                isSettlementChargeRefundRequiredToConsumer
              ) {
                setIsAddSapNumberModalOpen(true);
              } else {
                setRefundAmountPaymentModalProps({
                  applicationId: id,
                  utilityConsumerId,
                });
              }
            } else {
              if (
                applicationTypeId ===
                  applicationTypes["Permanent Connection"] ||
                applicationTypeId === applicationTypes["Temporary Connection"]
              ) {
                setIsAddSapNumberModalOpen(true);
              } else {
                if (isDcsUtility && isDemandAlterationApplication) {
                  setConfirmationModalProps({
                    title: "Attention",
                    message:
                      "New Demand Will be Applicable from Today – Are You Sure?",
                    onCancel: () => setConfirmationModalProps(null),
                    onConfirm: () => {
                      // delay of 250 millisecond for the next modal to open
                      setConfirmationModalProps(null);
                      setTimeout(
                        () => setConfirmationModalPropsForReleaseConnection(),
                        250
                      );
                    },
                    variant: "error",
                  });
                } else {
                  setConfirmationModalPropsForReleaseConnection();
                }
              }
            }
          }}
        >
          <Tooltip
            title={t(
              isPostConnectionApplications
                ? "Application Process Completion"
                : "Release Connection"
            )}
          >
            <CheckCircleIcon
              fontSize="small"
              className="cursor-pointer"
              color="success"
            />
          </Tooltip>
        </IconButton>
      )}
      {isSettlementChargeRefundIconVisible && (
        <IconButton
          onClick={() => {
            setRefundAmountPaymentModalProps({
              applicationId: id,
              utilityConsumerId,
              isSettlementRefundProcess: true,
            });
          }}
        >
          <Tooltip title={t("Settlement Refund Process")}>
            <PostAddIcon color="primary" />
          </Tooltip>
        </IconButton>
      )}

      {!!taxInvoiceUrl && (
        <IconButton
          onClick={() => {
            setDocumentToPreview({
              url: getImageUrl(taxInvoiceUrl),
              documentTypeName: "Tax Invoice",
              documentType: "pdf",
              documentName: "Tax Invoice",
              handleClose: () => setDocumentToPreview(null),
            });
          }}
        >
          <Tooltip title={t("Tax Invoice")}>
            <DescriptionIcon color="primary" />
          </Tooltip>
        </IconButton>
      )}

      {/* Preview icon to view  uploaded application document */}
      {isPreviewPendingDocumentsIconVisible && (
        <IconButton onClick={() => previewDocumentsBtnHandler(id)}>
          <Tooltip title={t("Preview Partially Uploaded Documents")}>
            <PreviewPendingDocumentsIcon />
          </Tooltip>
        </IconButton>
      )}
      {/* view icon for history chart for application status  */}
      <IconButton onClick={() => setApplicationIdForChartModal(id)}>
        <Tooltip title={t("Application Status History Chart")}>
          <ChartIcon />
        </Tooltip>
      </IconButton>
    </Box>
  );
};

OtherApplicationActions.propTypes = {
  item: PropTypes.object,
  t: PropTypes.func,
  viewApplicationBtnHandler: PropTypes.func,
};

export default withTranslation()(OtherApplicationActions);
